/**
 * ThemeToggle - 테마 전환 컴포넌트
 *
 * 라이트/다크/시스템 테마를 전환하는 토글 버튼.
 *
 * Features:
 * - 3가지 테마 옵션 순환
 * - 접근성 지원 (키보드, 스크린 리더)
 * - 아이콘 애니메이션
 *
 * @module ThemeToggle
 */

import { useCallback } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme, type Theme } from '@/contexts/theme-context';

interface ThemeToggleProps {
  /**
   * 추가 CSS 클래스
   */
  className?: string;

  /**
   * 크기 변형
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * 라벨 표시 여부
   */
  showLabel?: boolean;
}

const THEME_CYCLE: Theme[] = ['light', 'dark', 'system'];

const THEME_LABELS: Record<Theme, string> = {
  light: '라이트 모드',
  dark: '다크 모드',
  system: '시스템 설정',
};

const SIZE_CLASSES = {
  sm: 'p-1.5',
  md: 'p-2',
  lg: 'p-2.5',
};

const ICON_SIZES = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

/**
 * ThemeToggle Component
 *
 * @example
 * ```tsx
 * // 기본 사용
 * <ThemeToggle />
 *
 * // 라벨 표시
 * <ThemeToggle showLabel />
 *
 * // 크기 조절
 * <ThemeToggle size="lg" />
 * ```
 */
export function ThemeToggle({ className = '', size = 'md', showLabel = false }: ThemeToggleProps) {
  const { theme, setTheme, resolvedTheme } = useTheme();

  // 다음 테마로 순환
  const handleToggle = useCallback(() => {
    const currentIndex = THEME_CYCLE.indexOf(theme);
    const nextIndex = (currentIndex + 1) % THEME_CYCLE.length;
    setTheme(THEME_CYCLE[nextIndex]);
  }, [theme, setTheme]);

  // 현재 테마에 맞는 아이콘 선택
  const IconComponent = theme === 'system' ? Monitor : resolvedTheme === 'dark' ? Moon : Sun;

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={`
        inline-flex items-center gap-2 rounded-lg transition-colors
        ${SIZE_CLASSES[size]}
        text-gray-600 hover:text-gray-900 hover:bg-gray-100
        dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        dark:focus:ring-offset-gray-900
        ${className}
      `}
      aria-label={`현재: ${THEME_LABELS[theme]}. 클릭하여 테마 변경`}
      title={THEME_LABELS[theme]}
    >
      <IconComponent
        className={`
          ${ICON_SIZES[size]}
          transition-transform duration-200
          hover:rotate-12
        `}
        aria-hidden="true"
      />
      {showLabel && <span className="text-sm font-medium">{THEME_LABELS[theme]}</span>}
    </button>
  );
}

export default ThemeToggle;
